import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, CheckCircle, RefreshCw, Zap, User, Mail, Phone, Calendar, MessageSquare, Home, X } from 'lucide-react';
import escalationService from '../../../services/escalationService';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';


const SERVICE_LABELS = {
  architecte: 'Architecte', geometre: 'Géomètre', decorateur: 'Décorateur',
  demenageur: 'Déménageur', nettoyage: 'Nettoyage', electricien: 'Électricien',
  plombier: 'Plombier', menuisier: 'Menuisier', climatisation: 'Climatisation',
  securite: 'Sécurité',
};

const UrgencyBadge = () => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200 animate-pulse">
    <AlertTriangle className="w-3 h-3" />
    URGENT
  </span>
);

const TimeAgo = ({ date }) => {
  if (!date) return null;
  const hours = Math.round((Date.now() - new Date(date)?.getTime()) / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const label = days > 0 ? `${days}j ${hours % 24}h` : `${hours}h`;
  return (
    <span className={`text-xs font-medium ${hours >= 48 ? 'text-red-600' : hours >= 24 ? 'text-orange-600' : 'text-yellow-600'}`}>
      {label} sans réponse
    </span>
  );
};

const ResolveModal = ({ item, type, onConfirm, onCancel, loading }) => {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Marquer comme résolu</h3>
                <p className="text-sm text-gray-500">
                  {type === 'booking'
                    ? `Réservation de ${item?.client_name || 'client'}`
                    : `Demande #${item?.id?.slice(0, 8)}`}
                </p>
              </div>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Note de résolution (optionnel)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={3}
              placeholder="Ex: Professionnel contacté par téléphone, rendez-vous confirmé..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => onConfirm(note)}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Confirmer résolution
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EscalationPanel = () => {
  const [urgentBookings, setUrgentBookings] = useState([]);
  const [urgentInquiries, setUrgentInquiries] = useState([]);
  const [stats, setStats] = useState({ urgentBookings: 0, urgentInquiries: 0, pendingResolution: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, inquiriesRes, statsRes] = await Promise.all([
        escalationService?.getUrgentBookings(),
        escalationService?.getUrgentInquiries(),
        escalationService?.getEscalationStats(),
      ]);
      setUrgentBookings(bookingsRes?.data || []);
      setUrgentInquiries(inquiriesRes?.data || []);
      setStats(statsRes?.data || { urgentBookings: 0, urgentInquiries: 0, pendingResolution: 0, total: 0 });
    } catch (err) {
      console.error('[EscalationPanel] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTriggerCheck = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const { data, error } = await escalationService?.triggerEscalationCheck();
      if (error) {
        setTriggerResult({ success: false, message: error });
      } else {
        setTriggerResult({
          success: true,
          message: `${data?.escalated_bookings || 0} réservation(s) et ${data?.escalated_inquiries || 0} demande(s) escaladée(s).`,
        });
        await fetchData();
      }
    } finally {
      setTriggering(false);
      setTimeout(() => setTriggerResult(null), 5000);
    }
  };

  const handleResolve = async (note) => {
    if (!resolveModal) return;
    setResolving(true);
    try {
      if (resolveModal?.type === 'booking') {
        await escalationService?.resolveBookingEscalation(resolveModal?.item?.id, note);
      } else {
        const inquiry = resolveModal?.item;
        await escalationService?.resolveInquiryEscalation(inquiry?.id, note);

        // Send email + SMS notification to client about inquiry response
        const clientEmail = inquiry?.client_email;
        const clientPhone = inquiry?.client_phone;
        if ((clientEmail || clientPhone) && note?.trim()) {
          const TRANSACTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;
          const SMS_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms-confirmation`;
          const { data: sessionData } = await supabase?.auth?.getSession();
          const token = sessionData?.session?.access_token;
          const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
          const payload = {
            type: 'inquiry_response',
            data: {
              client_name: inquiry?.client_name || inquiry?.profiles?.full_name || '',
              client_email: clientEmail,
              client_phone: clientPhone,
              property_title: inquiry?.property?.title || '',
              property_type: inquiry?.property?.property_type || '',
              inquiry_message: inquiry?.message || '',
              response_message: note,
              agent_name: 'Équipe Kapital States',
            },
          };
          if (clientEmail) {
            fetch(TRANSACTIONAL_URL, { method: 'POST', headers, body: JSON.stringify(payload) })?.catch(e => console.warn('[EscalationPanel] Email notification failed:', e));
          }
          if (clientPhone) {
            fetch(SMS_URL, { method: 'POST', headers, body: JSON.stringify(payload) })?.catch(e => console.warn('[EscalationPanel] SMS notification failed:', e));
          }
        }
      }
      setResolveModal(null);
      await fetchData();
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {resolveModal && (
        <ResolveModal
          item={resolveModal?.item}
          type={resolveModal?.type}
          onConfirm={handleResolve}
          onCancel={() => setResolveModal(null)}
          loading={resolving}
        />
      )}
      {/* Header + Trigger */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              Escalades Automatiques — Demandes sans réponse
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Les demandes clients sans réponse du professionnel après 24h sont automatiquement escaladées.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
            <button
              onClick={handleTriggerCheck}
              disabled={triggering}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {triggering ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              Vérifier maintenant
            </button>
          </div>
        </div>

        {triggerResult && (
          <div className={`mt-4 p-3 rounded-lg text-sm font-medium ${triggerResult?.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {triggerResult?.success ? '✅' : '❌'} {triggerResult?.message}
          </div>
        )}
      </div>
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Urgents', value: stats?.total, color: 'red', icon: AlertTriangle },
          { label: 'Réservations', value: stats?.urgentBookings, color: 'orange', icon: Calendar },
          { label: 'Demandes Propriété', value: stats?.urgentInquiries, color: 'yellow', icon: Home },
          { label: 'En attente résolution', value: stats?.pendingResolution, color: 'purple', icon: Clock },
        ]?.map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
              <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                <Icon className={`w-4 h-4 text-${color}-600`} />
              </div>
            </div>
            <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
          </div>
        ))}
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { id: 'bookings', label: `Réservations urgentes (${urgentBookings?.length || 0})`, icon: Calendar },
              { id: 'inquiries', label: `Demandes urgentes (${urgentInquiries?.length || 0})`, icon: MessageSquare },
            ]?.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-red-500 text-red-600' :'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'bookings' && (
            <>
              {urgentBookings?.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Aucune réservation urgente</p>
                  <p className="text-gray-400 text-sm mt-1">Toutes les réservations ont reçu une réponse dans les 24h.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {urgentBookings?.map((booking) => (
                    <div key={booking?.id} className="border border-red-200 rounded-xl p-4 bg-red-50/30">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <UrgencyBadge />
                            <span className="text-sm font-semibold text-gray-800">
                              {SERVICE_LABELS?.[booking?.service_type] || booking?.service_type}
                            </span>
                            <TimeAgo date={booking?.created_at} />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-600">
                            {booking?.client_name && (
                              <span className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-gray-400" />
                                {booking?.client_name}
                              </span>
                            )}
                            {booking?.client_email && (
                              <span className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                {booking?.client_email}
                              </span>
                            )}
                            {booking?.client_phone && (
                              <span className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                {booking?.client_phone}
                              </span>
                            )}
                            {booking?.professional_name && (
                              <span className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-orange-400" />
                                Pro: {booking?.professional_name}
                              </span>
                            )}
                            {booking?.preferred_date && (
                              <span className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                {new Date(booking.preferred_date)?.toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                          {booking?.escalated_at && (
                            <p className="text-xs text-red-600">
                              Escaladé le {new Date(booking.escalated_at)?.toLocaleString('fr-FR')}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setResolveModal({ item: booking, type: 'booking' })}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex-shrink-0"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Résoudre
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'inquiries' && (
            <>
              {urgentInquiries?.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Aucune demande urgente</p>
                  <p className="text-gray-400 text-sm mt-1">Toutes les demandes ont reçu une réponse dans les 24h.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {urgentInquiries?.map((inquiry) => (
                    <div key={inquiry?.id} className="border border-red-200 rounded-xl p-4 bg-red-50/30">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <UrgencyBadge />
                            <span className="text-sm font-semibold text-gray-800">
                              {inquiry?.property?.title || 'Demande de renseignement'}
                            </span>
                            <TimeAgo date={inquiry?.created_at} />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-600">
                            {inquiry?.client_email && (
                              <span className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                {inquiry?.client_email}
                              </span>
                            )}
                            {inquiry?.client_phone && (
                              <span className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                {inquiry?.client_phone}
                              </span>
                            )}
                            {inquiry?.property?.address && (
                              <span className="flex items-center gap-1.5">
                                <Home className="w-3.5 h-3.5 text-gray-400" />
                                {inquiry?.property?.address}
                              </span>
                            )}
                          </div>
                          {inquiry?.message && (
                            <p className="text-xs text-gray-500 italic line-clamp-2">"{inquiry?.message}"</p>
                          )}
                          {inquiry?.escalated_at && (
                            <p className="text-xs text-red-600">
                              Escaladé le {new Date(inquiry.escalated_at)?.toLocaleString('fr-FR')}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setResolveModal({ item: inquiry, type: 'inquiry' })}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex-shrink-0"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Résoudre
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalationPanel;
